/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { ListeTable } from '@mairistem/liste';
import { List } from '@mairistem/composants';

import { type Individu, Sexe, ListeRecensement } from '@/types';
import { Adresse, Coordonnees } from '@/components/Listes/Components';
import dayjs from 'dayjs';

interface RecenseListeTableRowProps {
  item: Partial<Individu>;
  invalidate?: () => void;
  mode?: string;
  simplified?: boolean;
  selected?: boolean;
  displayRecensement?: boolean;
}

const LISTE_RECENSEMENT_LABELS: Record<ListeRecensement, string> = {
  [ListeRecensement.Recenses]: 'Recensés',
  [ListeRecensement.RecensesRegularises]: 'Recensés régularisés',
  [ListeRecensement.NonRecenses]: 'Non recensés',
  [ListeRecensement.OmisAdministratif]: 'Omis administratif',
};

const RecenseListeTableRow = (props: RecenseListeTableRowProps) => {
  const { item, invalidate, mode, selected, simplified, displayRecensement } =
    props;

  const {
    sexe,
    prenom,
    nomFamille,
    jourNaissance,
    moisNaissance,
    anneeNaissance,
    adresseNaissance,
    trimestreRecensement,
    anneeRecensement,
    dateRecensement,
    dateRecensementTracabilite,
    listeRecensementTracabilite,
    tel1,
    mail,
    adresseRattachement,
    adresseContact,
    recensement,
  } = item || {};

  // Sert à redimensionner les lignes comme il faut quand on passe
  // de la grande liste à la liste dockée
  // biome-ignore lint/correctness/useExhaustiveDependencies: no need
  React.useEffect(() => {
    invalidate?.();
  }, [mode]);

  if (simplified) {
    const dateNaissance = [
      jourNaissance && String(jourNaissance).padStart(2, '0'),
      moisNaissance && String(moisNaissance).padStart(2, '0'),
      anneeNaissance && String(anneeNaissance),
    ]
      .filter(Boolean)
      .join('/');

    return (
      <ListeTable.Row selected={selected}>
        <ListeTable.Cell name="recense">
          {`${(sexe === Sexe.Homme ? 'Monsieur ' : 'Madame ') + _.toUpper(nomFamille)} ${prenom}`}
        </ListeTable.Cell>
        <ListeTable.Cell name="dateNaissance">{dateNaissance}</ListeTable.Cell>
        <ListeTable.Cell name="lieuNaissance">
          <Adresse adresse={adresseNaissance} />
        </ListeTable.Cell>
      </ListeTable.Row>
    );
  }

  return (
    <ListeTable.Row selected={selected}>
      <ListeTable.Cell name="recense">
        {`${(sexe === Sexe.Homme ? 'Monsieur ' : 'Madame ') + _.toUpper(nomFamille)} ${prenom}`}
      </ListeTable.Cell>
      <ListeTable.Cell name="dateRecensement">
        {(() => {
          const dateAffichee = dateRecensementTracabilite || dateRecensement;
          const trimestreAffiche = dateRecensementTracabilite
            ? Math.floor(dayjs(dateRecensementTracabilite).month() / 3) + 1
            : trimestreRecensement;
          const anneeAffichee = dateRecensementTracabilite
            ? dayjs(dateRecensementTracabilite).year()
            : anneeRecensement;

          return (
            <List>
              {displayRecensement &&
                (() => {
                  const liste = listeRecensementTracabilite ?? recensement;
                  return (
                    liste && (
                      <List.Description>
                        {LISTE_RECENSEMENT_LABELS[liste]}
                      </List.Description>
                    )
                  );
                })()}
              {trimestreAffiche && (
                <List.Description>
                  {trimestreAffiche === 1
                    ? `1er trimestre ${anneeAffichee}`
                    : `${trimestreAffiche}ème trimestre ${anneeAffichee}`}
                </List.Description>
              )}
              {!_.isEmpty(dateAffichee) && (
                <List.Description>
                  {dayjs(dateAffichee).format('DD/MM/YYYY')}
                </List.Description>
              )}
            </List>
          );
        })()}
      </ListeTable.Cell>
      <ListeTable.Cell name="adresse">
        <Adresse
          adresse={
            !_.isNil(adresseRattachement) ? adresseRattachement : adresseContact
          }
        />
      </ListeTable.Cell>
      <ListeTable.Cell name="coordonnees">
        <Coordonnees telephone={tel1} mail={mail} />
      </ListeTable.Cell>
    </ListeTable.Row>
  );
};

export default RecenseListeTableRow;
